import {
  Callout,
  Checkbox,
  Classes,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Radio,
  RadioGroup,
  Tag,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { Dialog } from 'src/ui/components/Dialog';
import { FormAction, FormActions } from 'src/ui/components/FormActions';
import { Loading } from 'src/ui/components/Loading';
import {
  CLOUD_REPORT_MOVE_ACCESS_LIST,
  CLOUD_REPORT_MOVE_ACCESS_TOGGLE,
  CLOUD_REPORT_MOVE_ADD_ME,
  CLOUD_REPORT_MOVE_ALREADY_MEMBER,
  CLOUD_REPORT_MOVE_DIALOG,
  CLOUD_REPORT_MOVE_INELIGIBLE,
  CLOUD_REPORT_MOVE_INVALID_ORG,
  CLOUD_REPORT_MOVE_KEEP_SOURCE,
  CLOUD_REPORT_MOVE_MEMBER,
  CLOUD_REPORT_MOVE_MEMBERS,
  CLOUD_REPORT_MOVE_MULTIPLE_CONNECTIONS,
  CLOUD_REPORT_MOVE_ROLE_SELECT,
  CLOUD_REPORT_MOVE_TARGET_ORG_INPUT,
  CLOUD_REPORT_MOVE_VISIBILITY_ORG_WIDE,
  CLOUD_REPORT_MOVE_VISIBILITY_RESTRICTED,
} from 'src/ui/constants/testIds';
import { makeSubmitAction } from 'src/ui/containers/dialogs/makeSubmitAction';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  CloudConnectionMoveVisibility,
  useGetCloudConnectionMovePreviewQuery,
  useGetOrganizationMoveTargetQuery,
  useMoveCloudConnectionToOrgMutation,
  useProfileQuery,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

const dialogCopy = copy.components.moveConnectionToOrgDialog;

const MEMBER_ROLE = 'MEMBER';
const ADMIN_ROLE = 'ADMIN';
const DATABRICKS_LINKED_REASON = 'DATABRICKS_LINKED';
const ROLE_OPTIONS = [
  { label: dialogCopy.form.roleMember, value: MEMBER_ROLE },
  { label: dialogCopy.form.roleAdmin, value: ADMIN_ROLE },
];

type MoveConnectionToOrgDialogProps = {
  cloudId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const MoveConnectionToOrgDialog: React.VFC<MoveConnectionToOrgDialogProps> = ({
  cloudId,
  isOpen,
  onClose,
}) => {
  const isMounted = useIsMounted();
  const onMutate = useOnMutate();
  const onError = useOnError();
  const queryClient = useQueryClient();

  const [targetOrg, setTargetOrg] = React.useState('');
  const [visibility, setVisibility] = React.useState(
    CloudConnectionMoveVisibility.OrgWide
  );
  const [included, setIncluded] = React.useState<Record<string, boolean>>({});
  const [roles, setRoles] = React.useState<Record<string, string>>({});
  const [addMe, setAddMe] = React.useState(false);
  // Default to a non-destructive copy: create the org connection and access but
  // leave the existing personal connection(s) so users can swap over gradually.
  const [keepSource, setKeepSource] = React.useState(true);
  const [checkedDomain, setCheckedDomain] = React.useState<string | null>(null);

  const { data: profileData } = useProfileQuery(graphQLClient);
  const currentEmail = profileData?.profile?.email;

  const { data, isLoading } = useGetCloudConnectionMovePreviewQuery(
    graphQLClient,
    { cloud_id: cloudId },
    { enabled: isOpen }
  );
  const preview = data?.getCloudConnectionMovePreview;
  const access = React.useMemo(
    () =>
      (preview?.access ?? []).flatMap((entry) =>
        entry?.email
          ? [
              {
                email: entry.email,
                accepted: !!entry.accepted,
                ownsSeparateConnection: !!entry.owns_separate_connection,
                isCloudOwner: !!entry.is_cloud_owner,
              },
            ]
          : []
      ),
    [preview]
  );

  // Keep the per-email include/role maps in sync with the current access list:
  // default new emails (included, MEMBER) and prune any that are no longer present.
  React.useEffect(() => {
    const emails = access.map((entry) => entry.email);
    setIncluded((prev) =>
      Object.fromEntries(
        emails.map((email) => [email, email in prev ? prev[email] : true])
      )
    );
    setRoles((prev) =>
      Object.fromEntries(
        emails.map((email) => [
          email,
          email in prev ? prev[email] : MEMBER_ROLE,
        ])
      )
    );
  }, [access]);

  const trimmedOrg = targetOrg.trim();

  const moveTargetQuery = useGetOrganizationMoveTargetQuery(
    graphQLClient,
    { domain: checkedDomain ?? '' },
    { enabled: !!checkedDomain }
  );
  // A check result is only "current" when it matches the typed org.
  const target =
    checkedDomain === trimmedOrg
      ? moveTargetQuery.data?.getOrganizationMoveTarget
      : undefined;
  const isChecking = checkedDomain === trimmedOrg && moveTargetQuery.isFetching;
  const isChecked = !!target?.valid;
  const memberEmails = React.useMemo(
    () => new Set((target?.members ?? []).map((member) => member?.email ?? '')),
    [target]
  );

  const handleClose = () => {
    setTargetOrg('');
    setVisibility(CloudConnectionMoveVisibility.OrgWide);
    setIncluded({});
    setRoles({});
    setAddMe(false);
    setKeepSource(true);
    setCheckedDomain(null);
    onClose();
  };

  const {
    isLoading: isMoving,
    mutate: moveConnection,
  } = useMoveCloudConnectionToOrgMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: async (response) => {
      if (!response.moveCloudConnectionToOrg?.success) {
        return;
      }
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      await queryClient.refetchQueries(['getCloudReport']);
      /* istanbul ignore if */
      if (!isMounted.current) {
        return;
      }
      handleClose();
    },
  });

  const otherConnections = (preview?.other_connections ?? []).filter(
    Boolean
  ) as string[];

  const isEligible = preview ? preview.eligible !== false : true;
  const ineligibleMessage =
    preview?.ineligible_reason === DATABRICKS_LINKED_REASON
      ? dialogCopy.ineligibleDatabricks
      : dialogCopy.ineligible;
  const isWorking = isChecked ? isMoving : isChecking;
  const isSubmitDisabled = !trimmedOrg || !isEligible || isLoading || isWorking;

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    // Phase 1: resolve and inspect the target org before committing.
    if (!isChecked) {
      if (checkedDomain === trimmedOrg) {
        moveTargetQuery.refetch();
      } else {
        setCheckedDomain(trimmedOrg);
      }
      return;
    }

    // Phase 2: commit the move.
    const selected = access
      .filter((entry) => included[entry.email])
      .map((entry) => ({
        email: entry.email,
        organization_role: roles[entry.email] ?? MEMBER_ROLE,
      }));

    moveConnection({
      input: {
        cloud_id: cloudId,
        target_org: trimmedOrg,
        visibility,
        selected,
        add_current_user: addMe,
        keep_source: keepSource,
      },
    });
  };

  const actions: FormAction[] = [
    {
      text: dialogCopy.cancel,
      onClick: handleClose,
    },
    makeSubmitAction(
      isChecked ? dialogCopy.submit : dialogCopy.checkOrg,
      isWorking
    ),
  ];

  let submitTooltipText = '';
  if (!trimmedOrg) {
    submitTooltipText = dialogCopy.targetOrgRequired;
  }

  const members = target?.members ?? [];
  let membersSection: React.ReactNode = null;
  if (isChecked) {
    membersSection = (
      <FormGroup label={dialogCopy.form.members}>
        <div
          className="cloud-report__move-list"
          data-testid={CLOUD_REPORT_MOVE_MEMBERS}
        >
          {!members.length ? (
            <div className="cloud-report__move-access-item">
              <span className={Classes.TEXT_MUTED}>
                {dialogCopy.form.noMembers}
              </span>
            </div>
          ) : (
            members.map((member, idx) => (
              <div
                className="cloud-report__move-access-item"
                data-testid={CLOUD_REPORT_MOVE_MEMBER(member?.email ?? '', idx)}
                key={member?.email ?? idx}
              >
                {member?.email} <Tag minimal>{member?.role}</Tag>
              </div>
            ))
          )}
        </div>
      </FormGroup>
    );
  } else if (target && !target.valid) {
    // Inline error once an invalid org has been checked.
    membersSection = (
      <p
        className={Classes.TEXT_MUTED}
        data-testid={CLOUD_REPORT_MOVE_INVALID_ORG}
      >
        {dialogCopy.invalidOrg}
      </p>
    );
  }

  let body: React.ReactNode;
  if (isLoading) {
    body = <Loading />;
  } else if (!isEligible) {
    body = (
      <p data-testid={CLOUD_REPORT_MOVE_INELIGIBLE}>{ineligibleMessage}</p>
    );
  } else {
    body = (
      <form className="cloud-report__move-form" onSubmit={handleSubmit}>
        <FormGroup
          helperText={dialogCopy.form.targetOrgHelp}
          label={dialogCopy.form.targetOrg}
        >
          <InputGroup
            autoFocus
            data-testid={CLOUD_REPORT_MOVE_TARGET_ORG_INPUT}
            disabled={isMoving}
            onChange={(evt) => setTargetOrg(evt.target.value)}
            value={targetOrg}
          />
        </FormGroup>

        <div className="cloud-report__move-content">
          {isChecked ? (
            <FormGroup helperText={dialogCopy.keepSourceHelp}>
              <Checkbox
                checked={keepSource}
                data-testid={CLOUD_REPORT_MOVE_KEEP_SOURCE}
                disabled={isMoving}
                onChange={(evt) => setKeepSource(evt.currentTarget.checked)}
              >
                {dialogCopy.keepSource}
              </Checkbox>
              {!keepSource ? (
                <p className={Classes.TEXT_MUTED}>
                  {dialogCopy.keepSourceWarning}
                </p>
              ) : null}
            </FormGroup>
          ) : null}

          {membersSection}

          {isChecked ? (
            <>
              <RadioGroup
                label={dialogCopy.form.visibility}
                onChange={(evt) =>
                  setVisibility(
                    evt.currentTarget.value as CloudConnectionMoveVisibility
                  )
                }
                selectedValue={visibility}
              >
                <Radio
                  data-testid={CLOUD_REPORT_MOVE_VISIBILITY_ORG_WIDE}
                  disabled={isMoving}
                  label={dialogCopy.form.orgWide}
                  value={CloudConnectionMoveVisibility.OrgWide}
                />
                <Radio
                  data-testid={CLOUD_REPORT_MOVE_VISIBILITY_RESTRICTED}
                  disabled={isMoving}
                  label={dialogCopy.form.restricted}
                  value={CloudConnectionMoveVisibility.Restricted}
                />
              </RadioGroup>

              <FormGroup label={dialogCopy.form.access}>
                {!access.length ? (
                  <p className={Classes.TEXT_MUTED}>
                    {dialogCopy.form.accessEmpty}
                  </p>
                ) : (
                  <div
                    className="cloud-report__move-list"
                    data-testid={CLOUD_REPORT_MOVE_ACCESS_LIST}
                  >
                    {access.map(
                      (
                        {
                          email,
                          accepted,
                          ownsSeparateConnection,
                          isCloudOwner,
                        },
                        idx
                      ) => (
                        <div
                          className="cloud-report__move-access-item"
                          key={email}
                        >
                          <Checkbox
                            checked={!!included[email]}
                            data-testid={CLOUD_REPORT_MOVE_ACCESS_TOGGLE(
                              email,
                              idx
                            )}
                            disabled={isMoving}
                            onChange={(evt) => {
                              const { checked } = evt.currentTarget;
                              setIncluded((prev) => ({
                                ...prev,
                                [email]: checked,
                              }));
                            }}
                          >
                            {email}{' '}
                            <Tag minimal>
                              {accepted
                                ? dialogCopy.form.accepted
                                : dialogCopy.form.pending}
                            </Tag>{' '}
                            {isCloudOwner ? (
                              <Tag intent="primary" minimal>
                                {dialogCopy.form.owner}
                              </Tag>
                            ) : null}{' '}
                            {ownsSeparateConnection && !isCloudOwner ? (
                              <Tag minimal>{dialogCopy.form.ownConnection}</Tag>
                            ) : null}{' '}
                            {memberEmails.has(email) ? (
                              <Tag
                                data-testid={CLOUD_REPORT_MOVE_ALREADY_MEMBER(
                                  email,
                                  idx
                                )}
                                intent="warning"
                                minimal
                              >
                                {dialogCopy.form.alreadyMember}
                              </Tag>
                            ) : null}
                          </Checkbox>
                          <HTMLSelect
                            data-testid={CLOUD_REPORT_MOVE_ROLE_SELECT(
                              email,
                              idx
                            )}
                            disabled={isMoving || !included[email]}
                            onChange={(evt) => {
                              const { value } = evt.currentTarget;
                              setRoles((prev) => ({ ...prev, [email]: value }));
                            }}
                            options={ROLE_OPTIONS}
                            value={roles[email] ?? MEMBER_ROLE}
                          />
                        </div>
                      )
                    )}
                  </div>
                )}
              </FormGroup>

              <Checkbox
                checked={addMe}
                data-testid={CLOUD_REPORT_MOVE_ADD_ME}
                disabled={isMoving}
                onChange={(evt) => setAddMe(evt.currentTarget.checked)}
              >
                {dialogCopy.addMe}{' '}
                {currentEmail && memberEmails.has(currentEmail) ? (
                  <Tag intent="warning" minimal>
                    {dialogCopy.youAreMember}
                  </Tag>
                ) : null}
              </Checkbox>
            </>
          ) : null}
        </div>

        <input hidden type="submit" />
      </form>
    );
  }

  return (
    <Dialog
      canEscapeKeyClose={!isMoving}
      canOutsideClickClose={!isMoving}
      className="cloud-report__move-dialog"
      icon={IconNames.EXCHANGE}
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogCopy.title}
    >
      <div
        className={Classes.DIALOG_BODY}
        data-testid={CLOUD_REPORT_MOVE_DIALOG}
      >
        {otherConnections.length ? (
          <Callout
            data-testid={CLOUD_REPORT_MOVE_MULTIPLE_CONNECTIONS}
            intent="warning"
            title={dialogCopy.multipleConnections.title}
          >
            {dialogCopy.multipleConnections.body(otherConnections.join(', '))}
          </Callout>
        ) : null}
        {body}
      </div>
      <FormActions
        actions={actions}
        handleSubmit={handleSubmit}
        isSubmitDisabled={isSubmitDisabled}
        submitTooltipText={submitTooltipText}
      />
    </Dialog>
  );
};
